<template>
  <v-card>
    <v-card-text>
      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        v-model="selectedItems"
        @update:items-per-page="handleItemsPerPageUpdated"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="Öneriler"
            icon="mdi-head-lightbulb-outline"
            @clear-filters="$refs.headSearch.reset"
            @reload="loadList"
            :hide-edit="!can('edit-suggestion')"
            :edit-enabled="selectedItems.length === 1"
            @click:edit="handleEditClick"
            :search.sync="search.query"
            v-bind="titleBarAttrs"
            :add-route="
              can('edit-suggestion')
                ? { name: 'communication.suggestions.create' }
                : null
            "
            :add-enabled="clusterId"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.edit="{ item }">
          <router-link
            class="btn btn-icon btn-sm btn-clean"
            :to="{
              name: 'communication.suggestions.edit',
              params: { id: item.id },
            }"
          >
            <i class="menu-icon mdi mdi-pencil"></i>
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.provider="{ item }">
          <router-link
            v-if="item.provider_id"
            :to="{
              name: 'definitions.cluster-providers.show',
              params: { id: item.provider_id },
            }"
          >
            <rs-table-cell-string :value="item.provider" :limit="20" />
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.resident="{ item }">
          <router-link
            v-if="item.resident_id"
            :to="{
              name: 'definitions.residents.show',
              params: { id: item.resident_id },
            }"
          >
            <rs-table-cell-string :value="item.resident" :limit="14" />
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.subject="{ value }">
          <rs-table-cell-string :value="value" :limit="26" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.date="{ value }">
          <rs-table-cell-date :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.description="{ value }">
          <rs-table-cell-string :limit="50" :value="value" />
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasExport, hasPermissions } from "@/view/mixins";

export default {
  name: "SuggestionList",
  mixins: [filtersToURL, hasExport, hasPermissions],
  computed: {
    ...mapGetters(["companyId", "clusterId", "careStatusList"]),
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }),
      deep: true,
    },
    clusterId() {
      this.loadList();
    },
    companyId() {
      this.loadList();
    },
  },
  data() {
    return {
      titleBarAttrs: {
        exportUrl: "suggestions",
        exportParams: this.getParams,
      },
      search: {
        status_ids: [10, 1, 5],
      },
      options: {
        sortBy: ["date"],
        sortDesc: [false],
      },
      headers: [
        {
          text: "",
          value: "edit",
          searchable: false,
          sortable: false,
          width: "30px",
          align: "center",
          hide: () => !this.can("edit-suggestion"),
        },
        {
          text: "TYA",
          value: "cluster",
          searchable: "cluster",
          multiple: true,
          hide: () => this.clusterId,
        },
        {
          text: "Tarih",
          value: "date",
          searchable: "date",
          width: "40px",
        },
        {
          text: "Konu",
          value: "subject",
          searchable: "text",
          width: "220px",
        },
        {
          text: "Öneri Giderecek",
          value: "provider",
          searchable: "text",
          width: "180px",
        },
        {
          text: "Açıklama",
          value: "description",
          searchable: "text",
          width: "400px",
        },
        {
          text: "Öneri Bildiren",
          value: "resident",
          searchable: "text",
          width: "150px",
        },
        {
          text: "Öneri Durumu",
          value: "status",
          searchValue: "status_ids",
          searchable: "multiselect",
          options: () => this.careStatusList,
          sortable: false,
          width: "280px",
        },
      ],
    };
  },
  methods: {
    ...mapActions(["updateSuggestionCount", "updateSuggestionRecentList"]),
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      if (this.clusterId) {
        params.cluster_id = this.clusterId;
      }

      if (this.companyId) {
        params.company_id = this.companyId;
      }

      return params;
    },
    loadList() {
      if (this.isLoading) {
        this.resetTable();
        return false;
      }

      this.resetTable();
      this.isLoading = true;

      const params = this.getParams();

      this.$api
        .query("suggestions", { params })
        .then((response) => {
          if (response.data && response.data.data) {
            this.list = response.data.data;
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.itemsPerPage = response.data.meta.per_page;
            this.options.itemsPerPage = this.itemsPerPage;
            this.footTotals = response.data.meta.totals;
            this.updateSuggestionCount(response.data.meta.total);
            this.updateSuggestionRecentList(response.data.data);
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleEditClick() {
      if (this.isLoading || this.selectedItems.length !== 1) {
        return false;
      }

      this.$router.push({
        name: "communication.suggestions.edit",
        params: {
          id: this.selectedItems[0].id,
        },
      });
    },
  },
  mounted() {
    this.generateExportColumns(this.headers);
  },
};
</script>
